import { action, observable } from "mobx";
import { FormulaFormVM } from "../template/FormulaFormVM";
import MessageCenter from "cd-eft/components/common/MessageCenter/MessageCenterVM";
import { HydraulicFormulaController } from "cd-eft/services/eftSetApi";
import { TrapezoidalChannelRequestDto, TrapezoidalChannelResponseDto } from "cd-eft/_codegen_/openapi-generator/eft-set";
interface ITrapezoidalForm {
  bottomWidth: string,
  depth: string,
  sideSlopeZ1: string,
  sideSlopeZ2: string,
  bedSlope: string,
  mannings: string,
}
interface ITrapezoidalResult {
  hydraulicRadius: string,
  topWidth: string,
  area: string,
  capacityQ: string,
  valocity: string,
  criticalDepth: string,
}

export class TrapezoidalChannelVM extends FormulaFormVM {
  readonly formLabelsForExport;
  readonly resultLabelsForExport;
  constructor(props) {
    super(props);
    // Any Form specific contructor code. Otherwise, remove contructor - use parent.
    this.formLabelsForExport = {
      bottomWidth: "Bottom Width (ft)",
      depth: "Depth (ft)",
      sideSlopeZ1: "Side Slope 'Z1' (ft/ft)",
      sideSlopeZ2: "Side Slope 'Z2' (ft/ft)",
      bedSlope: "Bed Slope (ft/ft)",
      mannings: "Manning's n",
    }
    this.resultLabelsForExport = {
      hydraulicRadius: 'Hydraulic Radius (ft)',
      topWidth: 'Top Width (ft)',
      area: 'Area (sq ft)',
      capacityQ: 'Capacity Q (cfs)',
      valocity: 'Velocity (ft/sec)',
      criticalDepth: 'Critical Depth (ft)'
    }
    this.helpLink = 'https://usdagcc.sharepoint.com/:b:/r/sites/FPAC-NRCS-EngineeringPlanningAnalysisandDesignSoftwareEPAD/Shared%20Documents/General/NETS-Support%20Documents/Formulas%20Documentation/Help%20Select%20n%20Value.pdf?csf=1&web=1&e=ssYi6q';
    this.disablePrintBtn = true;
  }

  // When print button is clicked, print saved input values rather than current input values
  @observable SavedTrapezodialFormData: ITrapezoidalForm = {
    bottomWidth: "0",
    depth: "0",
    sideSlopeZ1: "0",
    sideSlopeZ2: "0",
    bedSlope: "0",
    mannings: "0",
  }

  @observable trapezodialFormData: ITrapezoidalForm = {
    bottomWidth: "0",
    depth: "0",
    sideSlopeZ1: "0",
    sideSlopeZ2: "0",
    bedSlope: "0",
    mannings: "0",
  }

  @observable formValidation = {
    hasError: true,
    bottomWidth: false,
    depth: false,
    sideSlopeZ1: false,
    sideSlopeZ2: false,
    bedSlope: false,
    mannings: false,
    formErrors: {
      bottomWidth: '',
      depth: '',
      sideSlopeZ1: '',
      sideSlopeZ2: '',
      bedSlope: '',
      mannings: '',
    },
  };

  readonly initialCalculationResults: ITrapezoidalResult = {
    hydraulicRadius: '',
    topWidth: '',
    area: '',
    capacityQ: '',
    valocity: '',
    criticalDepth: ''
  }
  // To properly override the calculationResults property in the TrapezoidalChannelVM class with the specific type ITrapezoidalResult
  @observable calculationResults: ITrapezoidalResult = this.initialCalculationResults;

  @action.bound TrapezoidalErrorClass(field) {
    const errorMessage = this.formValidation.formErrors[field];
    return errorMessage?.length === 0 ? '' : ' usa-input--error';
  }

  @action.bound setFixedValue(name, fixed) {
    const decimalPlaces = {
      "mannings-n": 4,
      "bed-slope": 4,
    };
    const event1 = {
      target: {
        name: name,
        value: this.roundingHelperString(fixed, decimalPlaces[name] ?? 2)
      }
    }
    this.handleInputChange(event1, true);
  }

  // Expand with onChange handlers, onBlur validation, Submit handler, on submit validation, individual variables, etc.
  @action.bound handleInputChange(event, within?:boolean) {
    const { name } = event.target;
    let value = event.target.value
    if (!within && value.length > 7) { //within variable to not check for lenght if triggered from setFixedValue function
      return
    }

    switch (name) {
      case 'bottom-width':
        this.trapezodialFormData.bottomWidth = value;
        break;
      case 'depth':
        this.trapezodialFormData.depth = value;
        break;
      case 'side-slope-z1':
        this.trapezodialFormData.sideSlopeZ1 = value;
        break;
      case 'side-slope-z2':
        this.trapezodialFormData.sideSlopeZ2 = value;
        break;
      case 'bed-slope':
        this.trapezodialFormData.bedSlope = value;
        break;
      case 'mannings-n':
        this.trapezodialFormData.mannings = value;

        break;
    }
  }

  @action.bound async validateInput(name, value) {
    let val;
    if (name === 'mannings-n') {
      val = this.roundingHelperNumber(parseFloat(value), 4);
    }
    else if (name === 'bed-slope') {
      val = this.roundingHelperNumber(parseFloat(value), 4);
    }
    else if (value !== undefined && value !== '') {
      val = this.roundingHelperNumber(parseFloat(value), 2);
    }
    else {
      val = 0;
    }
    if (value !== undefined && value !== '') {
      this.setFixedValue(name, val);
    }

    switch (name) {
      case 'bottom-width':
        if (value === '' || Number.isNaN(value) || val < 0.01) {
          this.formValidation.bottomWidth = true;
          this.formValidation.formErrors.bottomWidth = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.bottomWidth.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.bottomWidth = false;
          this.formValidation.formErrors.bottomWidth = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'depth':
        if (value === '' || Number.isNaN(value) || val < 0.01) {
          this.formValidation.depth = true;
          this.formValidation.formErrors.depth = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.depth.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.depth = false;
          this.formValidation.formErrors.depth = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'side-slope-z1':
        if (val < 0 || Number.isNaN(value)) {
          this.formValidation.sideSlopeZ1 = true;
          this.formValidation.formErrors.sideSlopeZ1 = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.sideSlopeZ1.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.sideSlopeZ1 = false;
          this.formValidation.formErrors.sideSlopeZ1 = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'side-slope-z2':
        if (val < 0 || Number.isNaN(value)) {
          this.formValidation.sideSlopeZ2 = true;
          this.formValidation.formErrors.sideSlopeZ2 = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.sideSlopeZ2.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.sideSlopeZ2 = false;
          this.formValidation.formErrors.sideSlopeZ2 = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'bed-slope':
        if (value === '' || Number.isNaN(value) || val <= 0) {
          this.formValidation.bedSlope = true;
          this.formValidation.formErrors.bedSlope = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.bedSlope.replace(/(\s\([\w\W]*\))/, ''))
        }
        else {
          this.formValidation.bedSlope = false;
          this.formValidation.formErrors.bedSlope = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'mannings-n':
        if (value === '' || Number.isNaN(value) || val < 0.0001 || val > 1) {
          this.formValidation.mannings = true;
          this.formValidation.formErrors.mannings = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Formula_02')
        }
        else {
          this.formValidation.mannings = false;
          this.formValidation.formErrors.mannings = '';
          this.formValidation.hasError = false;
        }
        break;
    }

  }

  @action.bound enableCalculateAndPrintButton() {
    if (this.formValidation.hasError) {
      this.disableComputeBtn = true;
      this.disablePrintBtn = true;
    }
    else {
      this.disableComputeBtn = false;
    }
  }
  get validateRequiredInputs() {
    if (this.formValidation.bedSlope || this.formValidation.bottomWidth || this.formValidation.depth || this.formValidation.mannings || this.formValidation.sideSlopeZ1 || this.formValidation.sideSlopeZ2) {
      return false;
    }
    return true;
  }
  async validateReqInput() {
    // Required
    await this.validateInput("bottom-width", this.trapezodialFormData.bottomWidth);
    await this.validateInput("depth", this.trapezodialFormData.depth);
    await this.validateInput("bed-slope", this.trapezodialFormData.bedSlope);
    await this.validateInput("mannings-n", this.trapezodialFormData.mannings);
    // Optional but should not be negative
    await this.validateInput("side-slope-z1", this.trapezodialFormData.sideSlopeZ1);
    await this.validateInput("side-slope-z2", this.trapezodialFormData.sideSlopeZ2);
  }

  @action.bound async computefnTrapezodial() {
    this.disableComputeBtn = true;
    MessageCenter.blockCalls(['Common_26']);
    await this.validateReqInput();
    if (this.validateRequiredInputs) {
      const requestPayload: TrapezoidalChannelRequestDto = {
        bedSlope: parseFloat(this.trapezodialFormData.bedSlope),
        bottomWidth: parseFloat(this.trapezodialFormData.bottomWidth),
        depth: parseFloat(this.trapezodialFormData.depth),
        manningsN: parseFloat(this.trapezodialFormData.mannings),
        sideSlopZ1: this.trapezodialFormData.sideSlopeZ1 ? parseFloat(this.trapezodialFormData.sideSlopeZ1) : 0,
        sideSlopZ2: this.trapezodialFormData.sideSlopeZ2 ? parseFloat(this.trapezodialFormData.sideSlopeZ2) : 0
      }
      try {
        const response = await HydraulicFormulaController.calculateTrapezoidalChannelUsingPOST(requestPayload);
        const responsePayload: TrapezoidalChannelResponseDto = response.data;
        if (this.hasErrorContent(responsePayload.errorResponse)) {
          MessageCenter.addNotification(responsePayload.errorResponse!.errorMessage!, responsePayload.errorResponse!.errorType!, responsePayload.errorResponse!.errorCode,
            responsePayload.errorResponse?.urlPath ? [{
              text: 'Help',
              callback: () => {
                window.open(responsePayload.errorResponse?.urlPath, '_blank');
              }
            }] : undefined)
        }
        if (responsePayload.validationPass) {
          this.SavedTrapezodialFormData = { ...this.trapezodialFormData };
          if (this.SavedTrapezodialFormData.sideSlopeZ1 === undefined || this.SavedTrapezodialFormData.sideSlopeZ1 === '') {
            this.SavedTrapezodialFormData.sideSlopeZ1 = "0";
          }
          if (this.SavedTrapezodialFormData.sideSlopeZ2 === undefined || this.SavedTrapezodialFormData.sideSlopeZ2 === '') {
            this.SavedTrapezodialFormData.sideSlopeZ2 = "0";
          }
          this.calculationResults = {
            hydraulicRadius: responsePayload.hydraulicRadius ? responsePayload.hydraulicRadius : '',
            topWidth: responsePayload.topWidth ? responsePayload.topWidth : '',
            area: responsePayload.area ? responsePayload.area : '',
            capacityQ: responsePayload.capacity ? responsePayload.capacity : '',
            valocity: responsePayload.valocity ? responsePayload.valocity : '',
            criticalDepth: responsePayload.criticalDepth ? responsePayload.criticalDepth : ''
          }
          this.hasResults = true;
          this.disablePrintBtn = false;
        }
        else {
          this.hasResults = false;
          this.disablePrintBtn = true;
          this.calculationResults = this.initialCalculationResults;
        }
      }
      catch (error) {
        this.hasResults = false;
        this.disablePrintBtn = true;
        this.calculationResults = this.initialCalculationResults;
        console.error('calculateTrapezoidalChannelUsingPOSTt: error =', error);
      }
      finally {
        this.disableComputeBtn = false;
      }
    } else {
      MessageCenter.resetMessageCenter();
      await MessageCenter.addNotificationFromCode('Common_26');
      this.hasResults = false;
      this.disablePrintBtn = true;
      this.disableComputeBtn = false;
    }
    MessageCenter.unblockCalls();
  }

  @action.bound launchAndExport() {
    // disable print button
    this.disablePrintBtn = true;
    this.exportedInputs = [];
    this.exportedResults = [];

    for (const input in this.SavedTrapezodialFormData) {
      this.exportedInputs.push({
        label: this.formLabelsForExport[input],
        value: this.SavedTrapezodialFormData[input]
      })
    }
    for (const result in this.calculationResults) {
      this.exportedResults.push({
        label: this.resultLabelsForExport[result],
        value: this.calculationResults[result]
      })
    }



    this.printFormula();
    this.disablePrintBtn = false;
  }
  @action.bound initializeDefaultValues(): void {
    this.trapezodialFormData = {
      bottomWidth: this.roundingHelperString(this.defaultValues.bottomWidth, 2),
      depth: this.roundingHelperString(this.defaultValues.depth, 2),
      sideSlopeZ1: this.roundingHelperString(this.defaultValues.sideSlopZ1, 2),
      sideSlopeZ2: this.roundingHelperString(this.defaultValues.sideSlopZ2, 2),
      bedSlope: this.roundingHelperString(this.defaultValues.bedSlope, 4),
      mannings: this.roundingHelperString(this.defaultValues.manningsN, 4),
    }
    this.SavedTrapezodialFormData = { ...this.trapezodialFormData };
  }
  @action.bound handleOnKeyDown(event) {
    ['e', 'E', '+', '-'].includes(event.key) && event.preventDefault();
  }
}